import { createRoot } from 'react-dom/client';
import { useCallback, useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';

function disposePopupRoot(popupRootRef) {
  const root = popupRootRef.current;
  popupRootRef.current = null;
  if (root) {
    queueMicrotask(() => root.unmount());
  }
}

function useMapPopup() {
  const popupRef = useRef(null);
  const popupRootRef = useRef(null);

  const closePopup = useCallback(() => {
    popupRef.current?.remove();
    popupRef.current = null;
    disposePopupRoot(popupRootRef);
  }, []);

  const openPopup = useCallback((map, lngLat, component, onClose) => {
    closePopup();

    const container = document.createElement('div');
    const root = createRoot(container);
    popupRootRef.current = root;
    root.render(component);

    const popup = new maplibregl.Popup({ closeButton: true, maxWidth: '80%' })
      .setLngLat(lngLat)
      .setDOMContent(container)
      .addTo(map);
    popupRef.current = popup;

    popup.on('close', () => {
      popupRef.current = null;
      disposePopupRoot(popupRootRef);
      onClose?.();
    });

    requestAnimationFrame(() => {
      if (popupRef.current) {
        popupRef.current.setLngLat(popupRef.current.getLngLat());
      }
    });
  }, [closePopup]);

  useEffect(() => {
    return () => {
      closePopup();
    };
  }, [closePopup]);

  return { openPopup, closePopup };
}

function useLayerHover(map, layerId) {
  useEffect(() => {
    if (!map || !layerId) return undefined;

    const handleMouseEnter = () => {
      map.getCanvas().style.cursor = 'pointer';
    };

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = '';
    };

    map.on('mouseenter', layerId, handleMouseEnter);
    map.on('mouseleave', layerId, handleMouseLeave);

    return () => {
      map.off('mouseenter', layerId, handleMouseEnter);
      map.off('mouseleave', layerId, handleMouseLeave);

      if (!map._removed) {
        map.getCanvas().style.cursor = '';
      }
    };
  }, [map, layerId]);
}

export { useMapPopup, useLayerHover };
